import { useState, useRef, useEffect, useMemo } from 'react';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Send, User, Bot, BookOpen, Sparkles, ArrowLeft } from "lucide-react";
import { useRequireAuth } from '@/hooks/use-auth-hooks';
import { useApiService } from '@/lib/api';
import { useSubjectCache } from '@/hooks/use-subject-cache';
import { useConversationCache } from '@/hooks/use-conversation-cache';
import { useDashboard } from '@/contexts/dashboard-context';
import { handleOAuthCallbackData } from '@/utils/oauth-callback-handler';
import { ChatErrorBoundary } from '@/components/error-boundaries/chat-error-boundary';
import { EmptyChatState } from '@/components/empty-chat-state';
import { AceRenderer } from '@/components/ui/ace-renderer';
import { formatSubjectName } from '@/utils/format';

// Enhanced chat message component with LaTeX and markdown support
const ChatMarkdown = ({ content, isUser = false }: { content: string; isUser?: boolean }) => {
  return (
    <div className={`text-sm ${isUser ? 'text-primary-foreground' : ''}`}>
            <AceRenderer 
        content={content} 
        compact={true} 
        enableMath={true} 
      />
    </div>
  );
};

interface Subject {
  id: string;
  name: string;
  icon?: string;
  color?: string;
}

interface Message {
  id: string;
  content: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
}

interface ChatHistoryItem {
  role: 'user' | 'teacher' | 'model' | 'assistant' | 'companion' | 'student' | 'evaluator';
  message: string;
  timestamp: string;
}

interface ChatResponse {
  _id: string;
  userId: string;
  title: string;
  history: ChatHistoryItem[];
  entityID: string;
  subjectID: string;
  isFavorite: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}



const CLASS_ID = "683190d373dc581e71e5657c"; // 9th class ID
//const CLASS_ID = "683190d373dc581e71e5657d"; // 10th class ID

export function DashboardPage() {
  const { user } = useRequireAuth();
  const apiService = useApiService();
  const { chatId } = useParams<{ chatId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedSubject, setSelectedSubject, isChatMode, setIsChatMode } = useDashboard();
  
  // Use subject cache instead of local state
  const { 
    subjects: rawSubjects, 
    hasLoaded: hasLoadedSubjects, 
    isLoading: isLoadingSubjects, 
    fetchSubjects 
  } = useSubjectCache(CLASS_ID);

  // Use conversation cache for refreshing chat history
  const { refreshConversations } = useConversationCache();
  
  // Transform subjects from cache to match dashboard interface
  const subjects: Subject[] = useMemo(() => {
    const transformedSubjects = rawSubjects.map(subject => ({
      id: subject.id,
      name: subject.display_name || subject.name, // Use display_name first, fallback to name
      icon: undefined,
      color: undefined
    }));
    return transformedSubjects;
  }, [rawSubjects]);
  
  // Load subjects when component mounts - only fetch if not cached
  useEffect(() => {
    if (!hasLoadedSubjects && !isLoadingSubjects) {
      fetchSubjects();
    }
  }, [hasLoadedSubjects, isLoadingSubjects, fetchSubjects]);
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [isLoadingChatHistory, setIsLoadingChatHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const loadedChatRef = useRef<string | null>(null); // Track loaded chat to prevent duplicate calls
  const loadingChatRef = useRef<string | null>(null); // Track currently loading chat

  // Retry function for error boundaries
  const handleRetryChat = () => {
    if (chatId) {
      loadChatFromUrl(chatId);
    } else {
      // If no chatId, go back to dashboard
      navigate('/dashboard');
    }
  };

  // Handle OAuth callback data if present (in case user was redirected here instead of /auth/callback)
  useEffect(() => {
    const result = handleOAuthCallbackData(searchParams);
    if (result.success) {
      // Force a small delay to ensure AuthProvider has updated, then stay on dashboard
      setTimeout(() => {
        // Check if we need to redirect to a cleaner URL
        if (location.pathname !== '/dashboard') {
          navigate('/dashboard', { replace: true });
        }
      }, 200);
    } else if (result.error) {
      console.error('DashboardPage: OAuth error:', result.error);
      // Redirect to login on OAuth error
      navigate('/login', { replace: true });
    }
  }, [searchParams, navigate, location.pathname]);

  // Set page title
  useEffect(() => {
    document.title = 'Dashboard - AceHorizon';
  }, []);

  // Check if we're in chat mode - update context when this changes
  const isLocalChatMode = location.pathname.startsWith('/dashboard/chat/');
  const isNewChatMode = location.pathname === '/dashboard' && selectedSubject;

  // Update context when chat mode changes
  useEffect(() => {
    const newChatMode = isLocalChatMode || (location.pathname === '/dashboard' && selectedSubject !== null);
    if (newChatMode !== isChatMode) {
      setIsChatMode(newChatMode);
    }
  }, [isLocalChatMode, isNewChatMode, isChatMode, setIsChatMode, selectedSubject, location.pathname]);

  // Handle subject selection
  const handleSubjectSelect = (subject: Subject) => {
    setSelectedSubject(subject);
    // Stay on /dashboard but show chat interface
  };

  // Handle going back to subject selection
  const handleBackToSubjects = () => {
    setSelectedSubject(null);
    setMessages([]);
    setCurrentChatId(null);
    // Stay on /dashboard route but show subject selection
  };

  // Function to load chat from URL parameter
  const loadChatFromUrl = async (chatId: string) => {
    // Prevent loading the same chat multiple times
    if (loadedChatRef.current === chatId && currentChatId === chatId) {
      return;
    }

    // Prevent loading if the same chat is currently being loaded
    if (loadingChatRef.current === chatId) {
      return;
    }

    try {
      setIsLoadingChatHistory(true);
      loadingChatRef.current = chatId; // Mark as currently loading
      
      // Reset state when loading a new chat
      setMessages([]);
      setSelectedSubject(null);
      
      const response = await apiService.get<ChatResponse>(`/chats/${chatId}`);

      if (response && response.subjectID) {
        // Find the subject by ID
        const subject = subjects.find(s => s.id === response.subjectID);
        if (subject) {
          setSelectedSubject(subject);
          setCurrentChatId(chatId);
          loadedChatRef.current = chatId; // Mark as successfully loaded
          
          // Handle empty history array gracefully
          if (response.history && Array.isArray(response.history) && response.history.length > 0) {
            // Convert API chat history to our Message format
            const chatMessages: Message[] = response.history.map((historyItem, index) => {
              const sender = historyItem.role === 'user' ? 'user' : 'assistant';
              return {
                id: (index + 1).toString(),
                content: historyItem.message,
                sender,
                timestamp: new Date(historyItem.timestamp)
              };
            });
            
            setMessages(chatMessages);
          } else {
            // Empty history - show welcome message for the subject
            const formattedSubjectName = formatSubjectName(subject.name);
            const welcomeMessage: Message = {
              id: '1',
              content: `Welcome back to ${formattedSubjectName} chat! 🎓\n\nThis chat doesn't have any history yet. I'm here to help you learn and understand concepts in ${formattedSubjectName}.\n\nWhat would you like to learn today?`,
              sender: 'assistant',
              timestamp: new Date()
            };
            setMessages([welcomeMessage]);
          }
        } else {
          console.error('Subject not found for chat:', { chatId, subjectID: response.subjectID });
          navigate('/dashboard');
        }
      } else {
        console.error('Invalid chat response:', { chatId, response });
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error loading chat:', { chatId, error });
      // Instead of immediately redirecting, show an error state
      // This allows the user to see what happened and potentially retry
      setMessages([{
        id: 'error-1',
        content: `Unable to load chat history. This might be due to:\n\n• The chat no longer exists\n• Network connectivity issues\n• Server temporarily unavailable\n\nYou can try refreshing the page or go back to the dashboard to start a new conversation.`,
        sender: 'assistant',
        timestamp: new Date()
      }]);
      
      // Set a placeholder subject so the UI doesn't break
      setSelectedSubject({
        id: 'error',
        name: 'Chat Loading Error'
      });
      
      loadedChatRef.current = null; // Reset on error to allow retry
      loadingChatRef.current = null; // Clear loading state
      
      // Don't navigate immediately - let user see the error and choose
      // navigate('/dashboard');
    } finally {
      setIsLoadingChatHistory(false);
      loadingChatRef.current = null; // Clear loading state regardless of success or failure
    }
  };

  // If in chat mode via URL but no subject selected, load chat or redirect
  useEffect(() => {
    if (location.pathname.startsWith('/dashboard/chat/') && chatId && hasLoadedSubjects) {
      // If we have a chatId, load the chat (even if we already have a subject selected, in case we're switching chats)
      if (chatId !== currentChatId) {
        loadChatFromUrl(chatId);
      }
    } else if (location.pathname.startsWith('/dashboard/chat/') && !chatId) {
      // If on chat URL but no chatId, redirect to dashboard
      navigate('/dashboard');
    }
  }, [location.pathname, chatId, navigate, hasLoadedSubjects, currentChatId]); // Use hasLoadedSubjects instead of subjects array

  // Reset state when navigating away from chat mode
  useEffect(() => {
    if (!isChatMode && currentChatId) {
      setCurrentChatId(null);
      setMessages([]);
      setSelectedSubject(null);
    }
  }, [isChatMode, currentChatId]);

  // Reset subject selection when navigating to plain dashboard
  useEffect(() => {
    if (location.pathname === '/dashboard' && !chatId) {
      setSelectedSubject(null);
      setMessages([]);
      setCurrentChatId(null);
    }
  }, [location.pathname, chatId]);

  // Load subjects using cache - only if not already loaded
  useEffect(() => {
    if (!hasLoadedSubjects && !isLoadingSubjects) {
      fetchSubjects().catch(error => {
        console.error('Failed to load subjects:', error);
      });
    }
  }, [hasLoadedSubjects, isLoadingSubjects, fetchSubjects]);

  // Function to load chat from URL parameter
  // Initialize chat when subject is selected (only for new chats, not when loading history)
  useEffect(() => {
    if (selectedSubject && !chatId) {
      // Only show welcome message for new chats (when there's no chatId in URL)
      // Reset chat ID when switching subjects
      setCurrentChatId(null);
      
      const formattedSubjectName = formatSubjectName(selectedSubject.name);
      const welcomeMessage: Message = {
        id: '1',
        content: `Welcome to ${formattedSubjectName} chat! 🎓\n\nI'm here to help you learn and understand concepts in ${formattedSubjectName}. You can ask me questions, request explanations, or use the suggested prompts below to get started.\n\nWhat would you like to learn today?`,
        sender: 'assistant',
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    }
  }, [selectedSubject, chatId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (messageText?: string, retryCount = 0) => {
    const text = messageText || inputValue.trim();
    if (!text || isLoading || !selectedSubject) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: text,
      sender: 'user',
      timestamp: new Date()
    };

    // Only add user message on first attempt (not retries)
    if (retryCount === 0) {
      setMessages(prev => [...prev, userMessage]);
      setInputValue('');
    }
    
    setIsLoading(true);
    let shouldRetry = false;

    try {
      // Prepare request payload - include chat ID if we have one
      const requestPayload: any = {
        prompt: text,
        subject_id: selectedSubject.id
      };
      
      // Include chat ID for subsequent messages (not the first message)
      if (currentChatId) {
        requestPayload.id = currentChatId;
      }

      const response = await apiService.postSSE<{
        success: boolean;
        data: {
          response: string;
          chatId?: string;
        };
      }>('/agents/smd/prompt', requestPayload);

      if (response.success && response.data?.response) {
        // Extract and store chat ID from the first response
        if (response.data.chatId && !currentChatId) {
          setCurrentChatId(response.data.chatId);
          // Refresh conversations to show new chat in history with a small delay
          setTimeout(() => {
            refreshConversations().catch(err => 
              console.warn('Failed to refresh conversations after new chat creation:', err)
            );
          }, 1000); // 1 second delay to ensure backend has processed the chat
        }
        
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: response.data.response,
          sender: 'assistant',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      let errorMessage = "I'm sorry, I'm having trouble connecting right now. Please try again in a moment.";
      
      // Handle specific error types with more helpful messages
      if (error instanceof Error) {
        const errorText = error.message.toLowerCase();
        
        if (errorText.includes('overloaded') || errorText.includes('503')) {
          if (retryCount < 2) { // Retry up to 2 times for overloaded errors
            shouldRetry = true;
            errorMessage = `🤖 AI service is busy (attempt ${retryCount + 2}/3). Retrying in a moment...`;
          } else {
            errorMessage = "🤖 The AI service is experiencing high demand. Please wait a few minutes and try again.";
          }
        } else if (errorText.includes('timeout') || errorText.includes('network')) {
          errorMessage = "🌐 Network connection issue. Please check your internet connection and try again.";
        } else if (errorText.includes('unauthorized') || errorText.includes('401')) {
          errorMessage = "🔒 Authentication error. Please refresh the page and try again.";
        } else if (errorText.includes('rate limit') || errorText.includes('429')) {
          errorMessage = "⏰ You're sending messages too quickly. Please wait a moment before trying again.";
        }
      }
      
      if (shouldRetry) {
        // Show retry message
        const retryMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: errorMessage,
          sender: 'assistant',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, retryMessage]);
        
        // Wait 2 seconds then retry
        setTimeout(() => {
          // Remove the retry message before retrying
          setMessages(prev => prev.filter(msg => msg.id !== retryMessage.id));
          handleSendMessage(text, retryCount + 1);
        }, 2000);
      } else {
        // Show final error message
        const errorResponseMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: errorMessage,
          sender: 'assistant',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorResponseMessage]);
      }
    } finally {
      // Only set loading to false if we're not retrying
      if (!shouldRetry) {
        setIsLoading(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    
    // Auto-resize textarea
    const target = e.target;
    target.style.height = 'auto';
    target.style.height = Math.min(target.scrollHeight, 128) + 'px';
  };

  // Show loading when in chat mode but chat history is being loaded OR when switching chats
  if (isLocalChatMode && (isLoadingChatHistory || (chatId && chatId !== currentChatId && subjects.length > 0))) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg font-medium">Loading chat...</p>
          <p className="text-sm text-muted-foreground">Setting up your conversation</p>
        </div>
      </div>
    );
  }

  // Subject selection view (show when no subject selected and not in existing chat mode)
  if (!selectedSubject && !isLocalChatMode) {
    return (
      <div className="flex flex-col h-full">
        
        <div className="flex-1 overflow-auto">
          <div className="container max-w-4xl mx-auto p-6 space-y-6">            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Choose a Subject</h2>
              <p className="text-muted-foreground">
                Select a subject from Class 9 to start your learning journey
              </p>
            </div>

            {isLoadingSubjects ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...Array(6)].map((_, i) => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-6">
                      <div className="h-6 bg-muted rounded mb-2"></div>
                      <div className="h-4 bg-muted rounded w-2/3"></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {subjects.map((subject) => (
                  <Card 
                    key={subject.id} 
                    className="cursor-pointer hover:shadow-md transition-shadow duration-200 hover:border-primary/50"
                    onClick={() => handleSubjectSelect(subject)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <BookOpen className="w-5 h-5 text-primary" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm truncate">{formatSubjectName(subject.name)}</h3>
                          <p className="text-xs text-muted-foreground">Class 9</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {!isLoadingSubjects && subjects.length === 0 && (
              <div className="text-center py-12">
                <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Subjects Found</h3>
                <p className="text-muted-foreground">
                  No subjects are available for Class 9 at the moment.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // If no subject is selected, don't render chat interface
  if (!selectedSubject) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg font-medium">Loading...</p>
          <p className="text-sm text-muted-foreground">Setting up your conversation</p>
        </div>
      </div>
    );
  }

  // Chat interface view
  return (
    <ChatErrorBoundary 
      chatId={chatId} 
      subjectName={selectedSubject?.name}
      onRetry={handleRetryChat}
    >
      <div className="flex flex-col h-[calc(100vh-4rem)] max-w-4xl mx-auto">
        {/* Header with back button */}
        <div className="flex-shrink-0 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10">
          <div className="flex items-center gap-4 p-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackToSubjects}
              title="Back to subjects"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-semibold">{formatSubjectName(selectedSubject.name)}</h1>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">Class 9</Badge>
                  <span className="text-sm text-muted-foreground">
                    {chatId ? 'Chat History' : 'New Chat'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Messages - Scrollable area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="p-4 space-y-4">
            {messages.length === 0 && !isLoading ? (
              <EmptyChatState 
                type="empty-history"
                subjectName={selectedSubject?.name}
                chatId={chatId}
              />
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex items-start space-x-3 ${
                    message.sender === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                  }`}
                >
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                message.sender === 'user' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted'
              }`}>
                {message.sender === 'user' ? (
                  <User className="w-4 h-4" />
                ) : (
                  <Bot className="w-4 h-4" />
                )}
              </div>
              
              <div className={`flex-1 min-w-0 ${
                message.sender === 'user' ? 'text-right' : ''
              }`}>
                <div className={`inline-block max-w-[85%] rounded-lg px-4 py-2 ${
                  message.sender === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted'
                }`}>
                  <ChatMarkdown 
                    content={message.content} 
                    isUser={message.sender === 'user'} 
                  />
                </div>
                <div className={`text-xs text-muted-foreground mt-1 ${
                  message.sender === 'user' ? 'text-right' : ''
                }`}>
                  {message.timestamp.toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>
            </div>
              ))
            )}
          {isLoading && (
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                <Bot className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <div className="bg-muted rounded-lg px-4 py-2 max-w-[85%]">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input - Fixed at bottom */}
      <div className="flex-shrink-0 p-4 bg-background border-t">
        <div className="relative">
          <div className="relative flex items-end gap-2 p-3 rounded-3xl border border-border bg-background shadow-lg hover:shadow-xl transition-all duration-200 focus-within:shadow-xl focus-within:border-ring/50 focus-within:ring-1 focus-within:ring-ring/20">
            <Textarea
              ref={textareaRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={`Ask me anything about ${formatSubjectName(selectedSubject.name)}... 💡`}
              className="flex-1 min-h-[24px] max-h-32 resize-none border-0 bg-transparent p-0 text-sm placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-muted"
              rows={1}
              style={{
                height: 'auto',
                minHeight: '24px',
                maxHeight: '128px',
                overflow: 'hidden'
              }}
            />
            <Button
              onClick={() => handleSendMessage()}
              disabled={!inputValue.trim() || isLoading}
              size="icon"
              className="h-8 w-8 rounded-full bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex-shrink-0 shadow-md hover:shadow-lg"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent" />
              ) : (
                <Send className="h-3 w-3" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground/70 mt-2 text-center">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
    </ChatErrorBoundary>
  );
}
